/**
 * Simulation Logic for Snakes and Ladders
 * Handles single and multi-game simulations with detailed tracking
 */

import { rollDice } from './Dice';

/**
 * Simulate a single move
 * @param {number} position - Current position
 * @param {object} config - Board configuration
 * @param {object} diceConfig - Dice configuration
 * @returns {object} Move result
 */
const simulateMove = (position, config, diceConfig) => {
    const { rows, cols, snakes, ladders } = config;
    const maxPosition = rows * cols;
    const roll = rollDice(diceConfig.count, diceConfig.sides);

    const start = position;
    let diceEnd = start + roll.total;
    let finalEnd = diceEnd;
    let hitSnake = null;
    let hitLadder = null;
    let won = false;

    // Check for win
    if (diceEnd >= maxPosition) {
        finalEnd = maxPosition;
        won = true;
    } else {
        // Check for snake
        for (const snake of snakes) {
            if (snake.start === diceEnd) {
                hitSnake = snake;
                finalEnd = snake.end;
                break;
            }
        }

        // Check for ladder (only if no snake)
        if (!hitSnake) {
            for (const ladder of ladders) {
                if (ladder.start === diceEnd) {
                    hitLadder = ladder;
                    finalEnd = ladder.end;
                    break;
                }
            }
        }
    }

    // Build path (sequential positions)
    const path = [start];
    for (let i = start + 1; i <= Math.min(diceEnd, maxPosition); i++) {
        path.push(i);
    }
    if (hitSnake || hitLadder) {
        path.push(finalEnd);
    }

    return {
        roll,
        start,
        diceEnd: Math.min(diceEnd, maxPosition),
        finalEnd,
        hitSnake,
        hitLadder,
        path,
        won
    };
};

/**
 * Generate move narrative
 * @param {object} move - Move object
 * @returns {string} - Human-readable narrative
 */
export const generateMoveNarrative = (move) => {
    const { roll, start, diceEnd, finalEnd, hitSnake, hitLadder, won } = move;

    let narrative = `Rolled a ${roll.total}, moved from ${start} to ${diceEnd}`;

    if (won) {
        narrative += `. YOU WIN!`;
    } else if (hitSnake) {
        narrative += `, that's a snake! Back to ${finalEnd}`;
    } else if (hitLadder) {
        narrative += `, nice ladder! Climbed to ${finalEnd}`;
    }

    return narrative;
};

/**
 * Simulate a complete game
 * @param {object} config - Board configuration
 * @param {object} diceConfig - Dice configuration { count, sides }
 * @param {number} startPosition - Starting position (default 1)
 * @returns {object} - Complete simulation result
 */
export const simulateGame = (config, diceConfig = { count: 1, sides: 6 }, startPosition = 1) => {
    const { rows, cols, snakes, ladders } = config;
    const maxPosition = rows * cols;

    const moves = [];
    const landingCounts = {};
    const snakeHits = {};
    const ladderHits = {};
    const diceDistribution = {};

    // Initialize tracking
    for (let i = 1; i <= maxPosition; i++) {
        landingCounts[i] = 0;
    }
    snakes.forEach(s => {
        snakeHits[`${s.start}-${s.end}`] = 0;
    });
    ladders.forEach(l => {
        ladderHits[`${l.start}-${l.end}`] = 0;
    });

    let position = startPosition;
    let won = false;
    let totalSnakesHit = 0;
    let totalLaddersHit = 0;

    // Mark starting position
    landingCounts[position] = (landingCounts[position] || 0) + 1;

    while (!won) {
        const move = simulateMove(position, config, diceConfig);
        moves.push(move);

        // Track dice distribution
        diceDistribution[move.roll.total] = (diceDistribution[move.roll.total] || 0) + 1;

        // Track landing
        landingCounts[move.finalEnd] = (landingCounts[move.finalEnd] || 0) + 1;

        // Track snake/ladder hits
        if (move.hitSnake) {
            const key = `${move.hitSnake.start}-${move.hitSnake.end}`;
            snakeHits[key] = (snakeHits[key] || 0) + 1;
            totalSnakesHit++;
        }
        if (move.hitLadder) {
            const key = `${move.hitLadder.start}-${move.hitLadder.end}`;
            ladderHits[key] = (ladderHits[key] || 0) + 1;
            totalLaddersHit++;
        }

        position = move.finalEnd;
        won = move.won;
    }

    return {
        config: {
            rows,
            cols,
            snakes,
            ladders,
            startPosition,
            dice: diceConfig
        },
        totalRolls: moves.length,
        snakesHit: totalSnakesHit,
        laddersHit: totalLaddersHit,
        moves,
        landingCounts,
        snakeHits,
        ladderHits,
        diceDistribution,
        won: true
    };
};

/**
 * Simulate multiple games and aggregate results
 * @param {number} numGames - Number of games to simulate
 * @param {object} config - Board configuration
 * @param {object} diceConfig - Dice configuration
 * @param {number} startPosition - Starting position
 * @returns {object} - Aggregated results
 */
export const simulateMultipleGames = (numGames, config, diceConfig = { count: 1, sides: 6 }, startPosition = 1) => {
    const { rows, cols, snakes, ladders } = config;
    const maxPosition = rows * cols;

    const results = [];
    const aggregatedLandingCounts = {};
    const aggregatedSnakeHits = {};
    const aggregatedLadderHits = {};
    const rollDistribution = {};

    // Initialize aggregates
    for (let i = 1; i <= maxPosition; i++) {
        aggregatedLandingCounts[i] = 0;
    }
    snakes.forEach(s => {
        aggregatedSnakeHits[`${s.start}-${s.end}`] = 0;
    });
    ladders.forEach(l => {
        aggregatedLadderHits[`${l.start}-${l.end}`] = 0;
    });

    let totalRolls = 0;
    let totalSnakesHit = 0;
    let totalLaddersHit = 0;
    let minRolls = Infinity;
    let maxRolls = 0;

    for (let i = 0; i < numGames; i++) {
        const result = simulateGame(config, diceConfig, startPosition);
        results.push({
            totalRolls: result.totalRolls,
            snakesHit: result.snakesHit,
            laddersHit: result.laddersHit
        });

        totalRolls += result.totalRolls;
        totalSnakesHit += result.snakesHit;
        totalLaddersHit += result.laddersHit;
        minRolls = Math.min(minRolls, result.totalRolls);
        maxRolls = Math.max(maxRolls, result.totalRolls);

        // Aggregate landing counts
        Object.entries(result.landingCounts).forEach(([square, count]) => {
            aggregatedLandingCounts[square] = (aggregatedLandingCounts[square] || 0) + count;
        });

        // Aggregate snake/ladder hits
        Object.entries(result.snakeHits).forEach(([key, count]) => {
            aggregatedSnakeHits[key] = (aggregatedSnakeHits[key] || 0) + count;
        });
        Object.entries(result.ladderHits).forEach(([key, count]) => {
            aggregatedLadderHits[key] = (aggregatedLadderHits[key] || 0) + count;
        });

        // Aggregate dice distribution
        Object.entries(result.diceDistribution).forEach(([roll, count]) => {
            rollDistribution[roll] = (rollDistribution[roll] || 0) + count;
        });
    }

    return {
        gamesSimulated: numGames,
        config: {
            rows,
            cols,
            snakes,
            ladders,
            startPosition,
            dice: diceConfig
        },
        averageRolls: Math.round((totalRolls / numGames) * 10) / 10,
        minRolls,
        maxRolls,
        averageSnakesHit: Math.round((totalSnakesHit / numGames) * 10) / 10,
        averageLaddersHit: Math.round((totalLaddersHit / numGames) * 10) / 10,
        aggregatedLandingCounts,
        aggregatedSnakeHits,
        aggregatedLadderHits,
        rollDistribution,
        gameResults: results
    };
};
